<template>
  <long-query-layout
    resource-name="tiger-i"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :customers.sync="customerCollection"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
  >
    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term-trial v-bind.sync="queryFilter.term" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="item" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <filter-category-item-category
          :company="queryFilter.item.company"
          v-bind.sync="queryFilter.item"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗" contentWidth="medium">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- 顧客グループ -->
      <query-filter-element id="customer" title="顧客グループ">
        <filter-customer v-bind.sync="queryFilter.customer" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option-trial-days" title="オプション">
        <filter-option-trial-days v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <gl-table ref="myTable" v-bind="resultTable" />
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTermTrial from '@/components/FilterTermTrial.vue'
import FilterCategoryItemCategory from '@/components/FilterCategoryItemCategory.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterCustomer from '@/components/FilterCustomer.vue'
import FilterOptionTrialDays from '@/components/FilterOptionTrialDays.vue'
// 分析条件モデル
import term from '@/queryfilter/termTiger'
import categoryItemCategory from '@/queryfilter/categoryItemCategory'
import store from '@/queryfilter/store'
import customer from '@/queryfilter/customer'
// 分析結果
import GlTable from '@/components/GlTable.vue'
import { searchCellCustomFunction } from '@/util/custom_formatters'

let hold = false

export default {
  components: {
    QueryFilterElement,
    FilterTermTrial,
    FilterCategoryItemCategory,
    FilterStore,
    FilterCustomer,
    FilterOptionTrialDays,
    LongQueryLayout,
    GlTable,
  },
  props: {
    /**
     * 自画面のドリルダウンや他画面からの遷移時に渡される引き継ぎID
     *
     * あくまで画面間で分析条件を引き継ぐための識別子で、分析時にサーバーで
     * 発行されるクエリIDとは別なので注意してください。
     *
     * メニューから最初に開く場合など、入力がない場合もあります。
     */
    inherit: {
      type: String,
    },
  },
  data() {
    return {
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, ...term.get() },
        item: {
          order: 2,
          summaryKey: 'categoryItemCategory',
          ...categoryItemCategory.get(),
        },
        store: { order: 3, ...store.get() },
        customer: { order: 4, ...customer.get() },
        option: {
          order: 5,
          summaryKey: 'optionTrialDays',
          trialDays: 365,
          errors: [],
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'code',
        sortOrder: 'asc',
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          linkify: (rowData, cell, value, colName) => {
            cell.classList.add('customer-collection-cell')
            cell.setAttribute(
              'data-customer-collection',
              rowData.customer_filter[colName],
            )

            const toggle = e => {
              if (!this.resultTable.columns) {
                throw new Error('Column not found')
              }
              // hit the column
              const col = this.resultTable.columns.find(
                c => c.colName === colName,
              )

              const id = e.target.getAttribute('data-customer-collection')
              const text = `${rowData.name} - ${col.headerText}`
              const customer = { id, text }

              this.$emit('toggle-customer-collection', customer)
            }

            cell.addEventListener('mousedown', e => {
              hold = true
              document.body.classList.add('no-select')
              toggle(e)
            })
            cell.addEventListener('mouseup', () => {
              hold = false
              document.body.classList.remove('no-select')
            })

            cell.addEventListener('mouseenter', e => {
              // console.log('hold', hold)
              if (!hold) {
                return
              }
              toggle(e)
            })
          },
          linkifySearch: searchCellCustomFunction,
        },
      },
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: term.mapToApiPayload(queryFilter.term),
        category:
          queryFilter.item.selectMode === 'category'
            ? categoryItemCategory.mapToApiPayload(queryFilter.item)
            : null,
        item:
          queryFilter.item.selectMode === 'category'
            ? null
            : categoryItemCategory.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        customer: customer.mapToApiPayload(queryFilter.customer),
        option: {
          trialDays: queryFilter.option.trialDays,
        },
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        categoryItemCategory.applyPayload(
          this.queryFilter.item,
          queryFilter.item,
        )
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        customer.applyPayload(this.queryFilter.customer, queryFilter.customer)
        this.queryFilter.option.trialDays = queryFilter.option.trialDays
      },
      /**
       * 顧客コレクション
       */
      customerCollection: [],
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
        }
      },
    }
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.itemGroupId = undefined
      return queryFilter
    },
  },
}
</script>
